<template>
  <div class="d-flex flex-column">
    <div class="grid-header d-flex justify-content-between align-items-center">
      <h1 class="mb-0">{{ $t("delegaciones_inmobiliarias.grid.titulo") }}</h1>
      <div class="toolbar">
        <button type="button" @click.prevent="crearDelegacion()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="gridProperties" :data-source="gridSource" ref="grid">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="Nombre" :caption="$t('delegaciones_inmobiliarias.grid.nombre')"></DxColumn>
      <DxColumn data-field="Descripcion" :caption="$t('delegaciones_inmobiliarias.grid.descripcion')"></DxColumn>
      <DxColumn
        data-field="Coordinadores"
        :caption="$t('delegaciones_inmobiliarias.grid.coordinadores')"
        cell-template="coordinadoresTemplate"
        :filter-operations="['contains']"
        :calculate-filter-expression="calculateFilterExpression"
      >
      </DxColumn>
      <DxColumn data-field="Telefono" :caption="$t('delegaciones_inmobiliarias.grid.telefono')"></DxColumn>
      <DxColumn data-field="Email" :caption="$t('delegaciones_inmobiliarias.grid.email')"></DxColumn>
      <DxColumn data-field="Web" :caption="$t('delegaciones_inmobiliarias.grid.web')"></DxColumn>
      <DxColumn data-field="DireccionFisica" :caption="$t('delegaciones_inmobiliarias.grid.direccion_fisica')"> </DxColumn>
      <DxColumn
        type="buttons"
        :caption="$t('delegaciones_inmobiliarias.grid.acciones')"
        :fixed="true"
        fixed-position="right"
        :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('delegaciones_inmobiliarias.grid.modificar'),
            icon: 'fa-regular fa-edit text-success',
            onClick: (e: any) => verDetalle(e.row?.data.Id),
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('delegaciones_inmobiliarias.grid.borrar'),
            icon: 'fa-regular fa-trash text-success',
            onClick: (e: any) => borrar(e.row?.data.Id),
            disabled: (options: any) => options.row?.data.TieneCoordinadores,
          },
        ]"
      />

      <template #coordinadoresTemplate="{ data }">
        <div v-if="data.value" class="coordinadores">
          <span
            v-for="(coordinador, index) in (data.value || '').split(',').map((role: string) => role.trim())"
            :key="coordinador"
            class="d-block rounded py-1 px-2 mr-2"
            :class="`coordinador`"
          >
            <p v-if="coordinador != ''" class="text-center mb-0">
              {{ coordinador }}
            </p>
          </span>
        </div>
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { DxDataGrid, DxColumn, DxColumnFixing } from "devextreme-vue/data-grid";
import { getDefaultGridProperties } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { useIntl } from "@services/i18n";
import { type DXLoadOptions } from "@/.shared/.api";
import { useModalService } from "@services/modal-service";
import { messageBoxResult } from "@components/dialogs/message-box";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import DelegacionInmobiliariaForm from "./DelegacionInmobiliariaForm.vue";
import { CreateDelegacionInmobiliaria } from "./models/CreateDelegacionInmobiliaria";

const intl = useIntl();
const api = useApi();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();
const gridProperties = ref<DxDataGrid>();

const props = defineProps({
  idEmpresaInmobiliaria: { type: String, required: true },
});

onMounted(async () => {
  setDatasource();
  gridProperties.value = getDefaultGridProperties();
});

async function crearDelegacion() {
  let title = `${intl.i18n.t("delegaciones_inmobiliarias.creacion.titulo")}`;
  var detalleEmpresaInmobiliaria = await api.callStandardApi("getEmpresaInmobiliariaById", { showLoading: false }, props.idEmpresaInmobiliaria!);
  let nombreEmpresaInmobiliaria = detalleEmpresaInmobiliaria.RazonSocial;

  modal
    .showModal(
      DelegacionInmobiliariaForm,
      { model: new CreateDelegacionInmobiliaria({ IdEmpresaInmobiliaria: props.idEmpresaInmobiliaria }), isEditMode: false },
      { title: title, width: 900, height: 700 },
    )
    .then((value) => {
      if (value == messageBoxResult.ok) grid.value?.instance?.refresh();
    });
}

const setDatasource = async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      if (q.filter != null && q.filter.length >= 0) q.filter = [["IdEmpresaInmobiliaria", "=", props.idEmpresaInmobiliaria], "and", q.filter];
      else q.filter = ["IdEmpresaInmobiliaria", "=", props.idEmpresaInmobiliaria];
      return await api.callStandardApi("getDelegacionesInmobiliariasForGrid", { showLoading: false }, q);
    },
  });
};

const verDetalle = async (idDelegacionInmobiliaria: string) => {
  let title = `${intl.i18n.t("delegaciones_inmobiliarias.edicion.titulo")}`;
  modal
    .showModal(
      DelegacionInmobiliariaForm,
      { model: await api.callStandardApi("getDelegacionInmobiliariaById", { showLoading: true }, idDelegacionInmobiliaria), isEditMode: true },
      { title: title, width: 800, height: 700 },
    )
    .then((value) => {
      if (value == messageBoxResult.ok) grid.value?.instance?.refresh();
    });
};

const borrar = async (idDelegacionInmobiliaria: string) => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("delegaciones_inmobiliarias.grid.borrar_pregunta"),
    intl.i18n.t("delegaciones_inmobiliarias.grid.titulo_confirma_borrar"),
    "okCancel",
    "warning",
  );
  if (respuesta == messageBoxResult.ok)
    await api
      .callStandardApi("deleteDelegacionInmobiliaria", { showLoading: true }, idDelegacionInmobiliaria)
      .then(() => {
        toastSuccess(intl.i18n.t("delegacion_inmobiliaria.borrado_exito"));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t("delegacion_inmobiliaria.borrado_fallo")));
};

function calculateFilterExpression(filterValue: string) {
  return [["Coordinadores", "contains", filterValue]];
}
</script>

<style lang="scss" scoped>



.icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 20px;
}

.coordinadores span:nth-child(n + 2) {
  margin-top: 5px;
}

.coordinador {
  color: white;
  background-color: #388b1fa1;
}
</style>
